// packages/components/src/calculator/components/SegmentSelector.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Card, RadioGroup, TextField, Tooltip } from '@dopomogai/ui';
import { Users, Info, Sparkles } from 'lucide-react';
import { useCalculator } from '@dopomogai/agent-kit';

type Props = {
  // Optional override if you want to control selection externally
  onSegmentChange?: (key: string | undefined) => void;
  showAuto?: boolean; // default true
  className?: string;
};

type Segment = {
  key: string;
  label: string;
  price_multiplier?: number;
  time_multiplier?: number;
};

type DetectionRule = {
  min?: number;
  max?: number;
  segmentKey: string;
};

export function SegmentSelector({ onSegmentChange, showAuto = true, className }: Props) {
  const { spec, state, actions } = useCalculator() as any;

  const segments: Segment[] = Array.isArray(spec?.segments) ? spec.segments : [];
  const detectionField: string | undefined = spec?.segment_detection?.field; // usually 'employee_count'
  const detectionRules: DetectionRule[] = Array.isArray(spec?.segment_detection?.rules)
    ? spec.segment_detection.rules
    : [];

  // Adapter: prefer calculator state's segmentKey if present
  const currentSegmentKey: string | undefined = (state as any)?.segmentKey || undefined;

  // Local UI state for "Auto" detection
  const [mode, setMode] = useState<'auto' | 'manual'>(showAuto ? 'auto' : 'manual');
  const [employeeCount, setEmployeeCount] = useState<string>('');

  // Compute a detected segment from rules
  const detectedKey = useMemo(() => {
    const n = Number(employeeCount);
    if (!Number.isFinite(n) || !detectionRules.length) return undefined;
    const hit = detectionRules.find((r) => {
      const minOk = typeof r.min === 'number' ? n >= r.min : true;
      const maxOk = typeof r.max === 'number' ? n <= r.max : true;
      return minOk && maxOk;
    });
    return hit?.segmentKey;
  }, [employeeCount, detectionRules]);

  // Unified setter: call parent, then calculator if available
  const applySegment = (key: string | undefined) => {
    onSegmentChange?.(key);
    if (actions && typeof (actions as any).setSegment === 'function') {
      (actions as any).setSegment(key);
    } else {
      // Soft fallback if calculator lacks setSegment — keep going without throwing
      // You can wire a custom setter in onSegmentChange when needed.
      // console.warn('SegmentSelector: calculator actions.setSegment is not available.');
    }
  };

  // When in auto mode with a detection hit, set the segment
  useEffect(() => {
    if (mode === 'auto' && detectedKey) {
      applySegment(detectedKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, detectedKey]);

  const manualKey = mode === 'manual' ? currentSegmentKey : detectedKey;

  return (
    <Card className={className} title="Company size" icon={<Users size={20} className="text-emerald-600" />}>
      <div className="space-y-4">
        {showAuto && (
          <div className="border-2 border-black rounded-xl overflow-hidden">
            <div className="p-4 bg-slate-50 border-b-2 border-black">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span className="font-bold">Auto‑detect by employee count</span>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                We’ll pick a sensible plan size; you can switch to manual any time.
              </p>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <RadioGroup
                  label="Mode"
                  value={mode}
                  onChange={(v) => setMode((v as any) || 'auto')}
                  options={[
                    { value: 'auto', label: 'Auto detect' },
                    { value: 'manual', label: 'Manual' },
                  ]}
                />
                {mode === 'auto' && (
                  <TextField
                    label="Employees"
                    type="number"
                    placeholder="e.g., 42"
                    value={employeeCount}
                    onChange={(e) => setEmployeeCount(e.target.value)}
                  />
                )}
              </div>

              {mode === 'auto' && (
                <div className="mt-3 text-sm">
                  {detectedKey ? (
                    <div className="inline-flex items-center gap-2 bg-emerald-50 border-2 border-black rounded-lg px-3 py-1.5">
                      <span className="font-semibold text-slate-800">Detected:</span>
                      <span className="font-bold">
                        {segments.find((s) => s.key === detectedKey)?.label || detectedKey}
                      </span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 text-xs text-slate-600">
                      <Info className="w-3.5 h-3.5" />
                      Enter a number to detect your size tier
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Select manually</div>
          <RadioGroup
            label="Business size"
            labelClassName="sr-only"
            value={manualKey}
            onChange={(v) => {
              setMode('manual');
              applySegment(v as string);
            }}
            options={segments.map((s) => ({
              value: s.key,
              label: s.label,
              description:
                s.price_multiplier && s.time_multiplier
                  ? `~${s.price_multiplier}× price, ~${s.time_multiplier}× time`
                  : undefined,
            }))}
          />
          {segments.length === 0 && (
            <div className="text-xs text-slate-500">
              No size tiers are configured for this agent.
            </div>
          )}
        </div>

        <div className="text-xs text-slate-600 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 mt-0.5" />
          <span>
            Size tiers adjust price and sometimes time to reflect integration complexity. You can still fine‑tune features below.
          </span>
        </div>
      </div>
    </Card>
  );
}

export default SegmentSelector;
